import { useState, useEffect } from 'react';
import './SdgInteractive.css';
import iconLogoImg from '../images/icon_logo.jpg';

const SDG_LIST = [
  { id: 1, title: 'No Poverty', category: 'People', color: '#E5243B', icon: '🤲', description: 'End poverty in all its forms everywhere by empowering vulnerable populations and expanding access to resources.' },
  { id: 2, title: 'Zero Hunger', category: 'People', color: '#DDA63A', icon: '🌾', description: 'End hunger, achieve food security and improved nutrition, and promote sustainable agriculture globally.' },
  { id: 3, title: 'Good Health & Well-being', category: 'People', color: '#4C9F38', icon: '🏥', description: 'Ensure healthy lives and promote well-being for all at all ages through essential health service access.' },
  { id: 4, title: 'Quality Education', category: 'People', color: '#C5192D', icon: '🎓', description: 'Ensure inclusive and equitable quality education and promote lifelong learning opportunities for all.' },
  { id: 5, title: 'Gender Equality', category: 'People', color: '#FF3A21', icon: '⚖️', description: 'Achieve gender equality and empower all women and girls across social, economic, and political spheres.' },
  { id: 6, title: 'Clean Water & Sanitation', category: 'Planet', color: '#26BDE2', icon: '💧', description: 'Ensure availability and sustainable management of water and sanitation for communities everywhere.' },
  { id: 7, title: 'Affordable & Clean Energy', category: 'Prosperity', color: '#FCC30B', icon: '⚡', description: 'Ensure access to affordable, reliable, sustainable and modern renewable energy for all.' },
  { id: 8, title: 'Decent Work & Economic Growth', category: 'Prosperity', color: '#A21942', icon: '📈', description: 'Promote sustained, inclusive economic growth, full productive employment, and decent work for all.' },
  { id: 9, title: 'Industry, Innovation & Infrastructure', category: 'Prosperity', color: '#FD6925', icon: '🏗️', description: 'Build resilient infrastructure, promote inclusive and sustainable industrialization, and foster innovation.' },
  { id: 10, title: 'Reduced Inequalities', category: 'People', color: '#DD1367', icon: '🤝', description: 'Reduce inequality within and among countries by strengthening social, economic, and political inclusion.' },
  { id: 11, title: 'Sustainable Cities & Communities', category: 'Prosperity', color: '#FD9D24', icon: '🏙️', description: 'Make cities and human settlements inclusive, safe, resilient, and environmentally sustainable.' },
  { id: 12, title: 'Responsible Consumption & Production', category: 'Planet', color: '#BF8B2E', icon: '🔄', description: 'Ensure sustainable consumption and production patterns through waste reduction and circular economies.' },
  { id: 13, title: 'Climate Action', category: 'Planet', color: '#3F7E44', icon: '🌍', description: 'Take urgent action to combat climate change and its impacts through mitigation, adaptation, and education.' },
  { id: 14, title: 'Life Below Water', category: 'Planet', color: '#0A97D9', icon: '🐠', description: 'Conserve and sustainably use the oceans, seas, and marine resources for sustainable development.' },
  { id: 15, title: 'Life on Land', category: 'Planet', color: '#56C02B', icon: '🌲', description: 'Protect, restore, and promote sustainable use of terrestrial ecosystems, manage forests, and halt biodiversity loss.' },
  { id: 16, title: 'Peace, Justice & Strong Institutions', category: 'Peace', color: '#00689D', icon: '🕊️', description: 'Promote peaceful and inclusive societies, provide access to justice for all, and build effective, accountable institutions.' },
  { id: 17, title: 'Partnerships for the Goals', category: 'Partnerships', color: '#19486A', icon: '🌐', description: 'Strengthen the means of implementation and revitalize global partnerships for sustainable development.' }
];

const CATEGORIES = ['All', 'People', 'Planet', 'Prosperity', 'Peace', 'Partnerships'];

function SdgInteractive() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedSdg, setSelectedSdg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Lock scroll & handle Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedSdg(null);
    };
    if (selectedSdg) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedSdg]);

  const filteredSdgs = SDG_LIST.filter((sdg) => {
    const matchesCategory = activeCategory === 'All' || sdg.category === activeCategory;
    const matchesSearch = sdg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          sdg.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="page-shell sdg-page">
      <div className="page-intro sdg-hero">
        <span className="sdg-pill">UN Agenda 2030</span>
        <h1 className="page-title">SDG Interactive</h1>
        <p className="page-subtitle">
          On September 25th, 2015, countries adopted a set of goals to end poverty, protect the prosperity for all as part of a new sustainable development agenda. Each goal has specific targets to be achieved over the next 15 years.
        </p>
      </div>

      {/* Topic Header with Icon Logo */}
      <div className="sdg-topic-header">
        <img
          src={iconLogoImg}
          alt="UNGDO Icon Logo"
          className="sdg-topic-icon"
        />
        <h2 className="sdg-topic-title">SUSTAINABLE DEVELOPMENT GOALS</h2>
      </div>

      {/* Filter and Search Bar */}
      <div className="sdg-filter-bar">
        <div className="category-pills">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`cat-pill ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="sdg-search-box">
          <input
            type="text"
            placeholder="Search goals or keywords..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* SDG 17 Cards Grid */}
      <div className="sdg-grid">
        {filteredSdgs.map((sdg) => (
          <div
            key={sdg.id}
            className="sdg-card"
            style={{ borderTopColor: sdg.color }}
            onClick={() => setSelectedSdg(sdg)}
          >
            <div className="sdg-card-header">
              <span className="sdg-number" style={{ background: sdg.color }}>Goal {sdg.id}</span>
              <span className="sdg-icon">{sdg.icon}</span>
            </div>
            <h3 className="sdg-title">{sdg.title}</h3>
            <p className="sdg-desc">{sdg.description.slice(0, 85)}...</p>
            <div className="sdg-card-footer">
              <span className="sdg-cat-tag">{sdg.category}</span>
              <span className="sdg-inspect-btn">Explore →</span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Detail View */}
      {selectedSdg && (
        <div className="sdg-modal-backdrop" onClick={() => setSelectedSdg(null)}>
          <div className="sdg-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sdg-modal-header" style={{ background: selectedSdg.color }}>
              <div className="sdg-modal-title-wrap">
                <span className="sdg-modal-icon">{selectedSdg.icon}</span>
                <div>
                  <span className="sdg-modal-badge">Goal {selectedSdg.id}</span>
                  <h2>{selectedSdg.title}</h2>
                </div>
              </div>
              <button className="sdg-modal-close" onClick={() => setSelectedSdg(null)}>✕</button>
            </div>

            <div className="sdg-modal-body">
              <div className="sdg-modal-meta">
                <span><strong>Category:</strong> {selectedSdg.category}</span>
                <span><strong>UN Alignment:</strong> 2030 Agenda</span>
              </div>
              <h4>Overview & UNGDO Initiatives</h4>
              <p>{selectedSdg.description}</p>

              <div className="sdg-action-box">
                <h5>How UNGDO Contributes:</h5>
                <ul>
                  <li>Facilitating targeted community projects aligned with Goal {selectedSdg.id}.</li>
                  <li>Mobilizing international donors and infrastructural funding for long-term impact.</li>
                  <li>Providing advocacy, education, and multi-stakeholder partnership building.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SdgInteractive;
